
import { Component, Vue, Watch } from 'vue-property-decorator'
import AdminUserForm from '@/components/admin/AdminUserForm.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn, ClientCatalogFilters, Domain, Client, UserCatalogGridItem, UserCatalogFilters, BasicEntity, User, Role, UserStatus, Group, RoleCatalogFilters, ClientWorkspacesFilters, Workspace } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import ImportFile from '@/components/generic/ImportFile.vue'
import GroupSelector from '@/components/generic/GroupSelector.vue'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { ADMIN_ROLES, PermissionNames, RoleNames } from '@/types/Auth'
import moment from 'moment'
import reportsService from '@/services/ReportsService'
import messageService from '@/services/support/MessageService'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'
import AdminLicensesList from '@/components/admin/AdminLicensesList.vue'
import ReleaseLicensesModal from '@/components/admin/users/ReleaseLicensesModal.vue'
import ResetLicenseDeviceModal from '@/components/admin/users/ResetLicenseDeviceModal.vue'
import { License, LicenseFilters } from '@/types/catalogs/Licenses'
import { Device } from '@/types/catalogs/Devices'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import { APIErrorFormat, ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminUserForm,
    DateSelector,
    TopToolBarPaginator,
    GroupSelector,
    AdminLicensesList,
    ReleaseLicensesModal,
    ResetLicenseDeviceModal,
    ImportFile,
    InputSelectorFilter
  }
})
export default class AdminUsers extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  MOBILE_UUID_SIZE = 16
  paginatorKey = 0
  currentPageIndex = 1
  pageIndexesLoaded: number[] = []
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  loadingDomains = false
  loadingClients = false
  loadingRoles = false
  loadingGroups = false
  leftSidebarCollapsed = false
  clients: BasicEntity[] = []
  domains: BasicEntity[] = []
  lastColumnSort: CatalogGridItemColumn
  // Grid columns definition
  columns: CatalogGridItemColumn[] = []
  orderOptions = [
    { value: 'id', text: 'ID' },
    { value: 'name', text: 'Nombre corto' },
    { value: 'firstName', text: 'Nombre' },
    { value: 'lastName', text: 'Apellido(s)' },
    { value: 'email', text: 'Correo' },
    { value: 'creationDate', text: 'Fecha de creación' },
    { value: 'updateDate', text: 'Fecha de actualización' }
  ]

  allUserItems: UserCatalogGridItem[] = []
  items: UserCatalogGridItem[] = []
  itemIds: number[] = []
  totalItems: number = 0
  filters = new UserCatalogFilters()
  domainIdSelected = -1
  selectedItem = new UserCatalogGridItem()
  selectedItems: UserCatalogGridItem[] = []
  clientsForFilter: BasicEntity[] = []
  clientSelected: BasicEntity = new BasicEntity(-1, '')
  groupsForFilter: Group[] = []
  groupSelectedFilter: Group = new Group()
  rolesForFilter: BasicEntity[] = []
  roleSelected: BasicEntity = new BasicEntity(-1, '')
  selectedAllUsers: boolean = false
  indeterminateUserSelection: boolean = false
  userSaved: boolean = false

  /** License History ********************/

  userLicenses: License[] = []
  userActiveLicenses: License[] = []
  userLicensesToRelease: License[] = []
  devicesToReset: Device[] = []
  fromAdminLicensesList: boolean = false
  updateAdminLicensesList: boolean = false

  importUserFields = [
    { key: 'name', label: 'Nombre' },
    { key: 'client', label: 'Cliente' },
    { key: 'domain', label: 'Dominio' },
    { key: 'role', label: 'Role' }
  ]

  async created () {
    this.columns = [
      CatalogGridItemColumn.def('name', 'Nombre corto', true),
      CatalogGridItemColumn.def('id', 'ID', true),
      CatalogGridItemColumn.def('statusName', 'Estado', true),
      CatalogGridItemColumn.def('domain', 'Dominio', true),
      CatalogGridItemColumn.def('client', 'Cliente', true),
      CatalogGridItemColumn.def('roleName', 'Rol', true),
      CatalogGridItemColumn.def('firstName', 'Nombre', true),
      CatalogGridItemColumn.def('lastName', 'Apellido(s)', true),
      CatalogGridItemColumn.def('email', 'Correo electrónico', true),
      CatalogGridItemColumn.def('cellPhone', 'Teléfono', true),
      CatalogGridItemColumn.def('groupsNames', 'Grupos', true),
      CatalogGridItemColumn.def('creationDateString', 'Fecha de creación', true),
      CatalogGridItemColumn.def('updateDateString', 'Fecha de actualización', true)
    ]

    if (this.canReadLicenses) {
      // add device uuid column after email column
      this.columns.splice(8, 0, CatalogGridItemColumn.def('device', 'Dispositivo (UUID)', true))
    }

    if (this.isRoleWithPermissions) {
      this.columns.push(CatalogGridItemColumn.def('disableDateString', 'Fecha de baja'))
      this.columns.push(CatalogGridItemColumn.def('billableString', 'Facturable'))
      this.columns.push(CatalogGridItemColumn.def('extendedPermissionsString', 'Permisos extendidos'))

      this.orderOptions.push({ value: 'disableDate', text: 'Fecha de baja' })
    }

    this.filters.domainId = this.isDomainRootUser ? userInfoStore.userInfo.domain.id : -1
    this.domainIdSelected = userInfoStore.userInfo.domain.id
    this.searchData(true)
    if (this.isDomainRootUser) {
      await this.loadDomainsPage(new PageRequest(this.PAGE_SIZE, 'name'))
      this.selectDefaultDomain()
    } else {
      this.loadFilters()
    }
  }

  /* User permissions */

  get createPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.USERS_CREATE)
  }

  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.USERS_UPDATE)
  }

  /** Checks if the user can delete users */
  get deletePermission () {
    return this.hasPermission(PermissionNames.USERS_DELETE)
  }

  /** Checks if the user can reset the password for users */
  get resetPasswordPermission () {
    return this.hasPermission(PermissionNames.USERS_RESETPASSWORD)
  }

  get isRoleWithPermissions () {
    return [RoleNames.OPERATIONS_ADMIN, RoleNames.FINANCES_ADMIN, RoleNames.FINANCES_CONTROL].includes(userInfoStore.userInfo.role.name)
  }

  get isRoleOperationsAdmin () {
    return userInfoStore.userInfo.role.name === RoleNames.OPERATIONS_ADMIN
  }

  get isDomainRootUser () {
    return userInfoStore.inRootDomain()
  }

  /**
   * @returns if the user has 'domains:read' permission
   */
  get canReadDomains () {
    return this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /**
   * @returns if the user has 'clients:read' permission
   */
  get canReadClients () {
    return this.hasPermission(PermissionNames.CLIENTS_READ)
  }

  /*
   * @returns if the user has 'workspaces:read' permission
   */
  get canReadWorkspaces () {
    return this.hasPermission(PermissionNames.WORKSPACES_READ)
  }

  /**
   * @returns if the user has 'roles:read' permission
   */
  get canReadRoles () {
    return this.hasPermission(PermissionNames.ROLES_READ)
  }

  /**
   * @returns if the user has 'groups:read' permission
   */
  get canReadGroups () {
    return this.hasPermission(PermissionNames.GROUPS_READ)
  }

  /**
   * @returns if the user has 'licenses:read' permission
   */
  get canReadLicenses () {
    return this.hasPermission(PermissionNames.LICENSES_READ)
  }

  /**
   * @returns if the user has 'licenses:delete' permission
   */
  get canDeleteLicenses () {
    return this.hasPermission(PermissionNames.LICENSES_DELETE)
  }

  /**
   * @returns if the user has 'devices:delete' permission
   */
  get canDeleteDevices () {
    return this.hasPermission(PermissionNames.DEVICES_DELETE)
  }

  /**
   * Checks if a user has a 'permission'
   * @param permission name to check
   */
  hasPermission (permission: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permission)
  }

  /** Import users */
  get importUsers () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.USERS_IMPORT)
  }

  /**
   * @returns true if the user can read de users belongs to the same domain,
   * otherwise false
  */
  get canReadDomainUsers () {
    return userInfoStore.hasPermission(PermissionNames.USERS_READ_DOMAIN)
  }

  /* Load data */

  /** @returns a page request with the configurations to get the user records */
  userPageRequest () {
    let pageRequest = new PageRequest<UserCatalogFilters>(this.PAGE_SIZE_VISIBLE_ITEMS)
    // when one of the two required dates for the filter by a date range is empty,
    // clear the dates array
    if (this.filters.fromTo && this.filters.fromTo.length > 0 && this.filters.fromTo[0] === null) {
      this.filters.fromTo = []
    }
    if (userInfoStore.inRootDomain() || this.canReadDomainUsers) {
      this.filters.domainId = this.domainIdSelected
    }
    pageRequest.sort = this.filters.order
    pageRequest.sortOrder = this.filters.sortOrder
    pageRequest.filters = this.filters
    pageRequest.filters.loadRelations = true
    pageRequest.page = this.currentPageIndex - 1
    return pageRequest
  }

  /**
   * Loads the user records
   */
  async searchData (updateTotalItems: boolean) {
    if (this.loadingData) return
    this.items = []
    let pageRequest = this.userPageRequest()
    await this.loadPage(pageRequest, updateTotalItems)
  }

  /**
   * Clears the paginator and research the users data
   */
  filterItems () {
    this.cleanLicenseDeviceValues()
    this.resetPaginator()
    this.searchData(true)
  }

  /**
   * Manages when a tab is selected for the
   * 'status' or 'license-type' filter,
   * the other tab must be cleared.
   */
  filterTabSelected (current: any, prev: any, event: any) {
    // When select 'status' filter
    if (current === 0) {
      this.filters.licenseType = ''
      this.filters.fromTo = []
      // When select 'license-type' filter
    } else if (current === 1) {
      this.filters.simpleStatus = ''
    }
  }

  /**
   * Loads user records
   * @param pageRequest configurations for request
   */
  async loadPage (pageRequest: PageRequest<UserCatalogFilters>, updateTotalItems: boolean) {
    this.loadingData = true
    catalogsService.getUsers(pageRequest)
      .then((response) => {
        if (updateTotalItems) {
          this.totalItems = response.total
        }
        this.items.push(...response.results.map((user: User) => {
          return this.fillGridItem(user, new UserCatalogGridItem())
        }))
      })
      .catch((error) => {
        let messageValues = {
          title: 'Error al cargar usuarios',
          message: '',
          delay: 4000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })
      .finally(() => {
        this.loadingData = false
        if (this.canReadLicenses) {
          this.updateLicensesRelated(0, 10)
        }
        if (this.lastColumnSort) {
          this.lastColumnSort.dir = this.lastColumnSort.dir === 'desc' ? 'asc' : 'desc'
          this.sortColum(this.lastColumnSort)
        }
      })
  }

  /**
   * Loads all user records
   * @param pageRequest configurations for request
   */
  async loadAllUsers (pageRequest: PageRequest<UserCatalogFilters>) {
    let totalItems = 0
    await catalogsService.getUsers(pageRequest)
      .then((response) => {
        totalItems = response.total
        this.allUserItems.push(
          ...response.results.map((user: User) => {
            return this.fillGridItem(user, new UserCatalogGridItem())
          })
        )
      })
      .catch((error) => {
        let messageValues = {
          title: 'Error al cargar usuarios',
          message: '',
          delay: 4000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })

    if (this.allUserItems.length < totalItems) {
      pageRequest.page++
      await this.loadAllUsers(pageRequest)
    }
  }

  async loadDomainsPage (pageRequest: PageRequest<any>) {
    this.loadingDomains = true
    await catalogsService.getDomains(pageRequest)
      .then((response) => {
        this.domains.push(...response.results.map((it: Domain) => {
          return { id: it.id, name: it.name }
        }))

        if (this.domains.length < response.total) {
          pageRequest.page++
          this.loadDomainsPage(pageRequest)
        }
      }).finally(() => { this.loadingDomains = false })
  }

  /**
   * Gets all clients from API
   * @param pageRequest configuration
   */
  async loadClients (pageRequest: PageRequest<any>) {
    this.loadingClients = true
    const response = await catalogsService.getClients(pageRequest)
    this.clientsForFilter.push(...response.results.map((it: Client) => {
      return new BasicEntity(it.id, it.name)
    }))

    if (this.clientsForFilter.length < response.total) {
      pageRequest.page++
      await this.loadClients(pageRequest)
    } else {
      this.loadingClients = false
    }
  }

  /**
   * Gets all workspaces from API based on the current domain
   */
  async loadClientsByWorkspaces (pageRequest: PageRequest<ClientWorkspacesFilters>) {
    this.loadingClients = true
    const response = await catalogsService.getWorkspacesByDomain(pageRequest)
    this.clientsForFilter.push(...response.results.map((it: Workspace) => {
      return new BasicEntity(it.client.id, it.client.name!)
    }))
    if (this.clientsForFilter.length < response.total) {
      pageRequest.page++
      await this.loadClientsByWorkspaces(pageRequest)
    } else {
      this.loadingClients = false
    }
  }

  /**
   * Gets all roles from API
   * @param pageRequest configuration
   */
  async loadRoles (pageRequest: PageRequest<any>) {
    this.loadingRoles = true
    catalogsService.getRoles(pageRequest)
      .then((response) => {
        this.rolesForFilter.push(
          ...response.results
            .filter((role: Role) =>
              this.isDomainRootUser
                ? true
                : !ADMIN_ROLES.includes(role.name as RoleNames))
            .map((role: Role) => { return new BasicEntity(role.id, role.name) })
        )
        let rolesLoaded = this.rolesForFilter.length
        let totalRoles = response.total
        if (this.isDomainRootUser && rolesLoaded < totalRoles) {
          pageRequest.page++
          this.loadRoles(pageRequest)
        } else if (!this.isDomainRootUser && rolesLoaded < totalRoles - ADMIN_ROLES.length) {
          pageRequest.page++
          this.loadRoles(pageRequest)
        }
      })
      .finally(() => { this.loadingRoles = false })
  }

  /**
   * Gets all groups that can access the requesting user from API
   * @param pageRequest configuration
   */
  async loadGroups (pageRequest: PageRequest<GroupCatalogFilters>) {
    this.loadingGroups = true
    catalogsService.getGroups(pageRequest)
      .then((response) => {
        this.groupsForFilter.push(...response.results.map((it: Group): Group => {
          return {
            id: it.id,
            name: it.name,
            subGroups: it.subGroups,
            status: it.status as GroupStatus,
            _selected: false } as Group
        }))
      })
      .finally(() => { this.loadingGroups = false })
  }

  /**
   * On select a role in the user filters updates the roleId value
   * for the request
   *
   * @param role selected
   */
  updateRoleSelected (role: BasicEntity) {
    this.filters.roleId = role.id
    this.roleSelected = role
  }

  /**
   * On select a client in the user filters updates the clientId value
   * for the request
   *
   * @param role selected
   */
  updateClientSelected (client: BasicEntity) {
    this.filters.clientId = client.id
    this.clientSelected = client
  }

  /**
   * Last selected group on tree groups
   * @param group - group selected for filter by
   */
  selectGroup (group: Group) {
    if (this.groupSelectedFilter.id === group.id) {
      this.groupSelectedFilter = new Group()
      this.filters.groupId = -1
    } else {
      this.groupSelectedFilter = group
      this.filters.groupId = group.id
    }
  }

  /* Grid and action events */

  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.LICENSES:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.updatePermission ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  selectItemAndShowDetail (item: any) {
    this.selectItem(item)
    this.queryItem()
  }

  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  cleanItemSelected () {
    this.selectedItem = new UserCatalogGridItem()
    this.selectedItems = []
    this.clearSelectedFlags()
  }

  /** Resets select user checboxes flags */
  clearSelectedFlags () {
    this.selectedAllUsers = false
    this.indeterminateUserSelection = false
  }

  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  newItem () {
    this.selectedItem = new UserCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }
  hideFeedback (): void {
    this.userSaved = false
  }

  showWorkspacesAdmin () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.WORKSPACES
    }
  }

  fillGridItem (origin: User, target: UserCatalogGridItem) {
    Object.assign(target, origin)
    target.statusName = User.getStatusDescription(origin.status)
    target.roleName = origin.role ? origin.role.name : ''
    target.domain = origin.domain ? origin.domain.name : ''
    target.client = origin.client ? origin.client.name : ''
    let phoneFormat = ''
    if (origin.cellPhone) {
      phoneFormat += origin.cellPhone.countryCode ? `(${origin.cellPhone.countryCode}) ` : '(- -) '
      phoneFormat += origin.cellPhone.number ? `${origin.cellPhone.number}` : '- - - - - - - - - -'
    } else {
      phoneFormat = '(- -)  - - - - - - - - - -'
    }
    target.cellPhone = phoneFormat
    target.creationDateString = origin.creationDate ? moment(origin.creationDate).format('DD/MM/YYYY') : '--'
    target.updateDateString = origin.updateDate ? moment(origin.updateDate).format('DD/MM/YYYY') : '--'
    target.disableDateString = origin.disableDate ? moment(origin.disableDate).format('DD/MM/YYYY') : '--'
    target.billableString = (origin.billable ? 'Sí' : 'No') as any
    target.extendedPermissionsString = (origin.extendedPermissions ? 'Sí' : 'No') as any
    target.groupsNames = origin.groups && origin.groups.length ? origin.groups.reduce((arr: string[], it: BasicEntity) => arr.concat(it.name), []).join(', ') : ''
    return target
  }

  /* Filter and sorting data */

  /**
   * Sets the available dates for 'from' and 'to' dates
   * in DateSelector for license-type param
   * @param date - in calendar
   * @param current - selected date in calendar
   */
  notIn31DaysRange (date: Date, current: Date[]) {
    let notInRange = false
    let today = moment().toDate()
    if (current.length > 0) {
      let minRangeDate = moment(current[0]).add(-31, 'days')
      let maxRangeDate = moment(current[0]).add(31, 'days')
      notInRange = date < minRangeDate.toDate() || date > maxRangeDate.toDate()
    }
    return date > today || notInRange
  }

  /**
   * Based on the users permissions load the client records
   */
  loadClientsBasedOnPermission () {
    if (this.isDomainRootUser) {
      if (this.canReadWorkspaces) {
        this.clientsForFilter = []
        let pageRequest = new PageRequest<ClientWorkspacesFilters>()
        pageRequest.filters.domainId = this.domainIdSelected
        this.loadClientsByWorkspaces(pageRequest)
          .then(() => {
            this.clientsForFilter.unshift(new BasicEntity(-1, '-- Seleccione un cliente --'))
            this.filters.clientId = -1
          })
      } else if (this.canReadClients) {
        this.clientsForFilter = []
        let pageRequest = new PageRequest<ClientCatalogFilters>()
        pageRequest.sort = 'name'
        this.loadClients(pageRequest)
          .then(() => {
            this.clientsForFilter.unshift(new BasicEntity(-1, '-- Seleccione un cliente --'))
            this.filters.clientId = -1
          })
      }
    }
  }

  /**
   * Loads the available filter for the user based on its permissions
   */
  async loadFilters () {
    // For filter by clients
    this.loadClientsBasedOnPermission()

    // For filter by roles
    if (this.canReadRoles) {
      let pageRequest = new PageRequest<RoleCatalogFilters>()
      if (this.isDomainRootUser) {
        pageRequest.filters.domain = this.domainIdSelected
      }
      this.rolesForFilter = []
      this.loadRoles(pageRequest)
        .then(() => {
          this.rolesForFilter.unshift(new BasicEntity(-1, '-- Seleccione un rol --'))
          this.filters.roleId = -1
        })
    }
    // For filter by groups
    if (this.canReadGroups) {
      let pageRequest = new PageRequest<GroupCatalogFilters>()
      if (this.isDomainRootUser) {
        pageRequest.filters.domain = this.domainIdSelected
      }
      this.groupsForFilter = []
      this.loadGroups(pageRequest)
    }
  }

  cleanFilter () {
    this.resetPaginator()
    let filters = new UserCatalogFilters()
    this.updateRoleSelected(new BasicEntity(-1, ''))
    this.updateClientSelected(new BasicEntity(-1, ''))
    filters.domainId = this.filters.domainId // Keeps the last filter domain value

    this.filters = filters
    if (userInfoStore.inRootDomain()) {
      this.filters.domainId = this.domainIdSelected
    } else {
      this.filters.domainId = -1
    }
    this.groupSelectedFilter = new Group()
    this.searchData(true)
  }

  cleanAndRefreshData () {
    this.cleanFilter()
    this.cleanItemSelected()
    this.filterItems()
  }

  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  parseColumDate (item: string) {
    const dateString = item.split('/')
    const newDate = new Date()
    newDate.setDate(parseInt(dateString[0]))
    newDate.setMonth(parseInt(dateString[1]) - 1)
    newDate.setFullYear(parseInt(dateString[2]))
    return newDate
  }

  sortingKey (item: String): [string, boolean] {
    return [item.toLowerCase(), item === item.toLowerCase()]
  }

  sortColum (col: CatalogGridItemColumn) {
    this.lastColumnSort = col
    if (col.key === 'device') {
      this.sortByLicenses(col)
    } else if (col.key === 'creationDateString' || col.key === 'updateDateString') {
      this.sortByDates(col)
    } else if (col.sort && col.key === 'id') {
      this.sortItems(col)
    } else {
      this.sortStrings(col)
    }
  }

  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.items, col)
  }

  sortStrings (col: CatalogGridItemColumn) {
    col.dir = col.dir === 'asc' ? 'desc' : 'asc'
    if (col.dir === 'asc') {
      this.items.sort((a: any, b: any) => {
        const keyA = this.sortingKey(`${(a as any)[col.key] ?? ''}`)
        const keyB = this.sortingKey(`${(b as any)[col.key] ?? ''}`)
        if (keyA[0] !== keyB[0]) {
          return keyA[0] > keyB[0] ? 1 : -1
        } else {
          return keyA[1] > keyB[1] ? 1 : -1
        }
      })
    } else {
      this.items.sort((a: any, b: any) => {
        const keyA = this.sortingKey(`${(a as any)[col.key] ?? ''}`)
        const keyB = this.sortingKey(`${(b as any)[col.key] ?? ''}`)
        if (keyA[0] !== keyB[0]) {
          return keyA[0] < keyB[0] ? 1 : -1
        } else {
          return keyA[1] < keyB[1] ? 1 : -1
        }
      })
    }
  }

  /**
   * Sorted by the number of licenses
   */
  sortByLicenses (col: CatalogGridItemColumn) {
    col.dir = col.dir === 'desc' ? 'asc' : 'desc'
    this.items.sort(function (a, b) {
      if (col.dir === 'asc') return a.activeLicenses.length > b.activeLicenses.length ? -1 : 1
      else return a.activeLicenses.length < b.activeLicenses.length ? -1 : 1
    })
  }

  sortByDates (col: CatalogGridItemColumn) {
    col.dir = col.dir === 'asc' ? 'desc' : 'asc'
    if (col.dir === 'asc') this.items.sort((a: any, b: any) => (this.parseColumDate(`${(a as any)[col.key]}`) > this.parseColumDate(`${(b as any)[col.key]}`)) ? 1 : -1)
    else this.items.sort((a: any, b: any) => (this.parseColumDate(`${(a as any)[col.key]}`) < this.parseColumDate(`${(b as any)[col.key]}`)) ? 1 : -1)
  }

  onPageChange (page: number) {
    this.cleanItemSelected()
    this.currentPageIndex = page
    this.searchData(false)
  }

  resetPaginator () {
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
    this.currentPageIndex = 1
  }

  inactiveItem () {
    this.$bvModal.msgBoxConfirm(`Una vez que el usuario se da de baja no es posible reactivarlo. ¿Desea continuar con la baja del usuario ${this.selectedItem.name}?`, {
      title: 'Confirmación',
      size: 'md',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(async (confirmed: boolean) => {
        if (confirmed) {
          await catalogsService.inactiveUser(this.selectedItem.id)
            .then(() => {
              this.selectedItem.status = UserStatus.INACTIVO
              this.selectedItem.statusName = User.getStatusDescription(UserStatus.INACTIVO)
              this.selectedItem.disableDateString = moment().format('DD/MM/YYYY')
              // unlock all active licenses from user selected
              this.unlockAllLicenses(this.selectedItem)
              messageService.success(
                'Baja existosa',
                `El usuario ${this.selectedItem.id} -${this.selectedItem.name}ha sido dado de baja de forma adecuada.`
              )
            })
            .catch((error) => {
              new MessageError({
                propsData: {
                  title: 'Error al dar de baja el usuario.',
                  error: APIErrorFormat.getInstance(error)
                }
              }).displayMessage()
            })
        }
      })
  }

  /**
   * Unlock all active licenses related to the user selected.
   *
   * @param user to unlock licenses
   */
  unlockAllLicenses (user: UserCatalogGridItem): void {
    let unlockLiceses: Promise<License>[] = []
    // attempt to unlock all licenses
    user.activeLicenses.forEach((license: License) => {
      unlockLiceses.push(catalogsService.deleteLicense(license.id))
    })
    Promise.all(unlockLiceses)
      .then(() => {
        this.setUserDeviceInfo(user, '')
      })
  }

  /**
   * Resets the current user password by API user
   */
  resetPass () {
    this.$bvModal.msgBoxConfirm(`¿Desea aplicar un reseteo de password para el usuario ${this.selectedItem.name}?`, {
      title: 'Confirmación',
      size: 'md',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(async (confirmed: boolean) => {
        if (confirmed) {
          catalogsService.resetUserPassword(this.selectedItem.id).then(() => {
            this.selectedItem.status = UserStatus.UPDATE_PASSWORD
            this.selectedItem.statusName = User.getStatusDescription(UserStatus.UPDATE_PASSWORD)
            this.selectedItem.updateDateString = moment().format('DD/MM/YYYY')
          }).catch((error) => {
            const apiError = APIErrorFormat.getInstance(error)
            new MessageError({
              propsData: {
                title: 'Error en el restablecimiento de contraseña.',
                error: apiError
              }
            }).displayMessage()
          })
        }
      })
  }

  /* Domain selector */

  domainSelectionOpen = false
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  searchDomain = ''

  showDomainSelection (flag: boolean) {
    if (flag) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.name && this.domainSelectedBackup.id !== this.domainSelected.id) {
          this.searchDomain = this.domainSelectedBackup.name
          this.domainSelected = this.domainSelectedBackup
        }
      }, 200)
    }
  }

  isMatchDomain (domain: any) {
    if (!this.searchDomain) return true
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  selectDomain (domain: BasicEntity) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    this.domainIdSelected = domain.id
    this.loadFilters()
  }

  selectDefaultDomain () {
    this.filters.domainId = userInfoStore.userInfo.domain.id
    this.domainIdSelected = userInfoStore.userInfo.domain.id
    const domain = this.domains.find(it => it.id === this.domainIdSelected)
    if (domain && domain.id >= 0) this.selectDomain(domain)
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  onItemSaved (item: User) {
    if (!this.selectedItem.id) {
      this.selectedItem = new UserCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
    this.showOnFeedbackUser()
  }

  showOnFeedbackUser () {
    this.userSaved = true
    setTimeout(() => {
      this.hideFeedback()
    }, 4500)
  }

  /** Import file */

  clientsByUpdatedUser: BasicEntity[] = []
  rolesbyUpdatedUser: BasicEntity[] = []

  /**
   * Creates a user instance based on a object with domain values
   * @param user - a object with user values to use
   * @returns newUser - a user instance from Catalogs format
   */
  async prepareUser (user: any) {
    try {
      let newUser = User.getIntanceForEdit()
      newUser.name = user.hasOwnProperty('name') ? user['name'] : null
      newUser.firstName = user.hasOwnProperty('firstName') ? user['firstName'] : null // optional
      newUser.lastName = user.hasOwnProperty('lastName') ? user['lastName'] : null // optional
      newUser.email = user.hasOwnProperty('email') ? user['email'] : null
      newUser.emailExtra = user.hasOwnProperty('emailExtra') ? user['emailExtra'] : null // optional
      newUser.billable = user.hasOwnProperty('billable') ? user['billable'] : null
      newUser.extendedPermissions = user.hasOwnProperty('extendedPermissions') ? user['extendedPermissions'] : null
      newUser.phoneExtra = user.hasOwnProperty('phoneExtra') ? user['phoneExtra'] : null // optional
      newUser.cellPhone = {
        countryCode: user.hasOwnProperty('countryCode') ? user['countryCode'] : null,
        number: user.hasOwnProperty('phoneNumber') ? user['phoneNumber'] : null
      }
      let groupList: string[] = user.hasOwnProperty('groups') ? user['groups'].toString().split(',') : []
      newUser.groups = groupList.map(g => new BasicEntity(parseInt(g), ''))
      // Sets client info
      newUser.client = {
        id: user.hasOwnProperty('clientId') ? user['clientId'] : null,
        name: user.hasOwnProperty('clientName') ? user['clientName'] : null
      }
      // Sets domain info based on current domain logged
      // or selected as filter when the user is root
      newUser.domain = {
        id: this.domainSelected.id || this.domainIdSelected,
        name: this.domainSelected.name || this.domainIdSelected.toString()
      }
      // Sets role info
      newUser.role = {
        id: user.hasOwnProperty('roleId') ? user['roleId'] : null,
        name: user.hasOwnProperty('roleName') ? user['roleName'] : null
      }
      let missingRequiredValues = newUser.validateRequiredValues()
      if (missingRequiredValues.length) {
        throw ErrorImport.getErrorRequiredValues(ImportationErrorAdvices.no_required_user_values)
      } else {
        let formatErrors = newUser.validateFormatValues()
        if (formatErrors.length) {
          throw ErrorImport.getErrorFormatValues(
            formatErrors,
            newUser.name
          )
        }
      }
      return newUser
    } catch (error) {
      LogService.error('Error durante la preparación de un usuario.')
      throw error
    }
  }

  /**
   * Based on the user parameter,
   * gets the user by id and assign it the new values
   * @param user - values to update
   * @returns a user object to save
   */
  async updatedUser (user: any) {
    try {
      let userEntity = new User()
      // Validates the user existence
      await catalogsService.getUserById(user.id)
        .then((res) => {
          Object.assign(userEntity, res, user)

          // Sets the same current domain with the required format
          // this cannot change to avoid change users between domains
          userEntity.domain = res.domain

          let cellPhoneObject = userEntity.cellPhone || { countryCode: '', number: '' }
          if (user['countryCode']) cellPhoneObject['countryCode'] = user['countryCode']
          if (user['phoneNumber']) cellPhoneObject['number'] = user['phoneNumber']
          userEntity.cellPhone = cellPhoneObject

          // Handles the case when the user does not have a client
          if (res.client && res.client.id && res.client.id > 0) {
            userEntity.client = res.client
          }
          userEntity.role = res.role

          let groupList: string[] = user.hasOwnProperty('groups') ? user['groups'].toString().split(',') : []
          if (groupList.length) {
            userEntity.groups = groupList.map(g => new BasicEntity(parseInt(g), ''))
          }
        })
        .catch((error) => {
          LogService.error(`Error en la consulta de un usuario: ${ToString(error.response ?? error)}`)
          throw new ErrorImport(
            ImportErrorCode.NO_RELATED_ENTITY,
            ErrorTitles.user_not_found,
            `El usuario con el ID: ${user.id} no se ha podido cargar.`,
            ImportationErrorAdvices.entity_not_found
          )
        })

      // When we want to update the client of the user
      // Validates the client existence
      if (user.hasOwnProperty('clientId')) {
        let clientId = user['clientId']
        // First look up in the client lists to avoid send an extra request
        let localClient: BasicEntity | undefined = new BasicEntity(-1, '')
        if (this.clientsForFilter.findIndex(e => e.id === clientId) > -1) {
          localClient = this.clientsForFilter.find((c: BasicEntity) => c.id === clientId)
        } else if (this.clientsByUpdatedUser.findIndex(c => c.id === clientId) > -1) {
          let clientFromUpdated = this.clientsByUpdatedUser.find((c: BasicEntity) => c.id === clientId) || new BasicEntity(-1, '')
          localClient.id = clientFromUpdated.id
          localClient.name = clientFromUpdated.name
        }
        if (localClient && localClient.id > 0) {
          userEntity.client = new BasicEntity(localClient.id, localClient.name)
        } else {
          await catalogsService.getClientById(clientId)
            .then((res) => {
              let clientConsulted = new BasicEntity(res.id, res.name)
              this.clientsByUpdatedUser.push(clientConsulted)
              userEntity.client = clientConsulted
            })
            .catch((error) => {
              LogService.error(`Error en la consulta de un cliente para el usuario: ${ToString(error.response ?? error)}`)
              throw new ErrorImport(
                ImportErrorCode.NO_RELATED_ENTITY,
                ErrorTitles.client_not_found,
                `El cliente con el ID: ${clientId} para el usuario ${user.id} no se ha podido cargar.`,
                ImportationErrorAdvices.entity_not_found
              )
            })
        }
      }

      // When we want to update the role of the user
      // Validates the role existence
      if (user.hasOwnProperty('roleId')) {
        let roleId = user['roleId']
        // First look up in the roles used for filters to avoid send an extra request
        let localRole: BasicEntity | undefined = new BasicEntity(-1, '')
        if (this.rolesForFilter.findIndex(e => e.id === roleId) > -1) {
          localRole = this.rolesForFilter.find((r: any) => r.id === roleId)
        } else if (this.rolesbyUpdatedUser.findIndex(r => r.id === roleId) > -1) {
          let roleFromUpdated = this.rolesbyUpdatedUser.find((r: BasicEntity) => r.id === roleId) || new BasicEntity(-1, '')
          localRole.id = roleFromUpdated.id
          localRole.name = roleFromUpdated.name
        }
        if (localRole && localRole.id > 0) {
          userEntity.role = new BasicEntity(localRole.id, localRole.name)
        } else {
          await catalogsService.getRoleById(roleId)
            .then((res) => {
              let roleConsulted = new BasicEntity(res.id, res.name)
              this.rolesbyUpdatedUser.push(roleConsulted)
              userEntity.role = roleConsulted
            })
            .catch((error) => {
              LogService.error(`Error en la consulta de un rol para el usuario: ${ToString(error.response ?? error)}`)
              throw new ErrorImport(
                ImportErrorCode.NO_RELATED_ENTITY,
                ErrorTitles.role_not_found,
                `El rol con el ID: ${roleId} para el usuario ${user.id} no se ha podido cargar.`,
                ImportationErrorAdvices.entity_not_found
              )
            })
        }
      }

      // When we want to update the domain of the user
      // Validates the domainId existence
      if (user.hasOwnProperty('domainId')) {
        let domainId = user['domainId']
        // First look up in the local domains to avoid send an extra request
        let localDomain = this.domains.findIndex(d => d.id === domainId) > -1
          ? this.domains.find((d: BasicEntity) => d.id === domainId)
          : new BasicEntity(-1, '')
        if (localDomain && localDomain.id > 0) {
          userEntity.domain = localDomain
        } else {
          await catalogsService.getDomainById(domainId)
            .then((res) => {
              userEntity.domain = new BasicEntity(res.id, res.name)
            })
            .catch((error) => {
              LogService.error(`Error en la consulta de un dominio para el usuario: ${ToString(error.response ?? error)}`)
              throw new ErrorImport(
                ImportErrorCode.NO_RELATED_ENTITY,
                ErrorTitles.domain_not_found,
                `El dominio con el ID: ${domainId} para el usuario ${user.id} no se ha podido cargar.`,
                ImportationErrorAdvices.entity_not_found
              )
            })
        }
      }

      let missingRequiredValues = userEntity.validateRequiredValues()
      if (missingRequiredValues.length) {
        throw ErrorImport.getErrorRequiredValues([
          `Lo siguientes valores son necesarios para poder actualizar: ${missingRequiredValues},
          verifica que la columna esta presente y/o que cuenta con un valor. Usuario: ${userEntity.id} - ${userEntity.name}`
        ])
      } else {
        let errorFormatValues = userEntity.validateFormatValues()
        if (errorFormatValues.length) {
          throw ErrorImport.getErrorFormatValues(
            errorFormatValues,
            userEntity.name
          )
        }
      }
      return userEntity
    } catch (error) {
      LogService.error('Error durante la actualización de valores de un usuario.')
      throw error
    }
  }

  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importUserFields' variable
   * @returns object with column values to use on import file action
   */
  userDetails (user: any) {
    return {
      name: user.name,
      client: user.client.name,
      domain: user.domain.name,
      role: user.role.name
    }
  }

  /**
   * Gets the save client function from catalogService to use in import component
   */
  get saveUser () {
    return catalogsService.saveUser
  }

  /**
   * Generates a page request to get the active licenses from a user
   *
   * @param user to get licenses
   */
  prepareRequestForActiveLicenses (user: UserCatalogGridItem) {
    let pageRequest = new PageRequest<LicenseFilters>()
    pageRequest.filters.userId = user.id
    pageRequest.filters.active = true
    return pageRequest
  }

  /**
   * Sets the current action to display the licenses component
   */
  showLicenses () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.LICENSES
    }
  }

  /**
   * Gets the licenses to delete from the current user selected.
   * If the parameter 'licenses' is not empty is because this method was
   * called from 'AdminLicensesList'.
   * At last, emits the event to display the confirmation modal.
   *
   * @param licenses to release
   */
  async showDeleteLicensesModal (licenses: License[] = []) {
    this.fromAdminLicensesList = false
    if (licenses.length > 0) {
      this.userLicensesToRelease = licenses
      this.fromAdminLicensesList = true
    }
    this.$bvModal.show('delete-licenses-modal')
  }

  /**
   * Gets the devices that can be deleted from the current user selected.
   * If the parameter 'devices' is set is because this method was
   * called from 'AdminLicensesList'. At last, emits the event to display
   * the confirmation modal.
   *
   * @param devices to reset
   */
  async showDeleteDevicesModal (devices: Device[] = []) {
    this.fromAdminLicensesList = false
    if (devices.length > 0) {
      this.devicesToReset = devices
      this.fromAdminLicensesList = true
    }
    this.$bvModal.show('delete-devices-licenses-modal')
  }

  /**
   * Gets the licenses with the pageRequest configurations
   *
   * @param pageRequest with query-parameters and/or filters
   */
  async getLicenses (pageRequest: PageRequest<LicenseFilters>) {
    let licenses: License[] = []
    await catalogsService.getLicenses(pageRequest)
      .then((response) => {
        licenses.push(...response.results)
      })
    return licenses
  }

  /**
   * Turns on the flag 'updateAdminLicensesList', that will help us
   * to reload the table on the AdminLicensesList component.
   * And consults the licenses records to enable/disable action buttons
   * for licenses.
   *
   * @param updateComponent
   */
  licensesReleased (updateComponente: boolean) {
    this.updateAdminLicensesList = updateComponente
    this.updateLicensesInfo(this.selectedItem)
    this.cleanLicensesToReset()
  }

  /**
   * Clears the devices to reset list
   * and updates the license info for each user selected
   */
  devicesReseted () {
    for (const user of this.selectedItems) {
      this.updateLicensesInfo(user)
    }
    this.cleanDevicesToReset()
  }

  /** Clears the devices to reset list */
  cleanDevicesToReset () {
    this.devicesToReset = []
  }

  /** Clean the licenses to unlock */
  cleanLicensesToReset () {
    this.userLicensesToRelease = []
  }

  /**
   * Clean the devices previously selected
   * Clean the licenses previously selected
   * And reset the flags for the checboxes
   */
  cleanLicenseDeviceValues () {
    this.clearSelectedFlags()
    this.cleanDevicesToReset()
    this.cleanLicensesToReset()
  }

  /**
   * Set the UUID device for a user row
   *
   * @param user to set its UUID
   * @param uuid to set
   */
  setUserDeviceInfo (user: UserCatalogGridItem, uuid: string): void {
    Vue.set(user, 'device', uuid)
  }

  /**
   * Updates the device info for the user item
   * and display the device info in the grid
   */
  updateDevicesInfo (user: UserCatalogGridItem) {
    if (user.activeLicenses.length) {
      user.activeDevices = user.activeLicenses.map(l => { return l.device })
      let deviceString = user.activeDevices.map(d => { return d.uuid }).join(', ')
      this.setUserDeviceInfo(user, deviceString)
    } else {
      user.activeDevices = []
      this.setUserDeviceInfo(user, '')
    }
  }

  /**
   * @returns true if the uuid from a device is short than 16 chars
   */
  isMobileDevice (device: Device) {
    return device.uuid.length <= this.MOBILE_UUID_SIZE
  }

  /**
   * Adds/removes all devices from the active licenses
   * for all users that has one to the devices to reset list
   *
   * @param event - checkbox state; adds if it is true, otherwise removes
   */
  addAllToReset (event: any) {
    for (const user of this.items) {
      if (event) {
        Vue.set(user, '_selected', true)
        this.addToReset(true, user)
      } else {
        Vue.set(user, '_selected', false)
        this.addToReset(false, user)
      }
    }
  }

  /**
   * Adds/removes the device from the active license
   * by the user selected to the devices to reset list
   *
   * @param event - checkbox state; adds if it is true, otherwise removes
   * @param user - row selected by checbox
   */
  addToReset (event: any, user: UserCatalogGridItem) {
    if (event) {
      // add the user to selected items
      let userIndex = this.selectedItems.findIndex(u => u.id === user.id)
      if (userIndex === -1) this.selectedItems.push(user)
      // add the user mobile devices
      let userMobileDevices = user.activeDevices.filter(this.isMobileDevice)
      for (const device of userMobileDevices) {
        if (this.devicesToReset.findIndex(d => d.id === device.id) === -1) {
          this.devicesToReset.push(device)
        }
      }
      // add the active license by user to consider to unlock
      this.addUserLicensesToUnlock(user)
    } else {
      // remove the devices from the user
      let deviceIndex
      for (const device of user.activeDevices) {
        deviceIndex = this.devicesToReset.findIndex(d => d.id === device.id)
        if (deviceIndex > -1) {
          this.devicesToReset.splice(deviceIndex, 1)
        }
      }
      // remove the active license by user to avoid to unlock
      this.removeUserLicensesToUnlock(user)
      // remove the user from selected items
      let userIndex = this.selectedItems.findIndex(u => u.id === user.id)
      this.selectedItems.splice(userIndex, 1)
    }
  }

  /**
   * Add the user licenses to consider to unlock by the modal
   *
   * @param user to get the active licenses
   */
  addUserLicensesToUnlock (user: UserCatalogGridItem) {
    this.userLicensesToRelease = this.userLicensesToRelease.concat(user.activeLicenses)
  }

  /**
   * Remove the user licenses to not be considered to unlock by the modal
   *
   * @param user to get the active licenses and remove them
   */
  removeUserLicensesToUnlock (user: UserCatalogGridItem) {
    for (const license of user.activeLicenses) {
      let licenseIndex = this.userLicensesToRelease
        .findIndex(unlockLicense => unlockLicense.id === license.id)
      if (licenseIndex !== -1) this.userLicensesToRelease.splice(licenseIndex, 1)
    }
  }

  /**
   * Watches the changes for the selected items list
   * and manages the states of the user selection checkboxes
   *
   * @param oldUsers - previous user selected list
   * @param newUsers - current user selected list
  */
  @Watch('selectedItems')
  selectedItemsWatch (currentUsers: UserCatalogGridItem[], previousUsers: UserCatalogGridItem[]) {
    if (currentUsers.length === 0) {
      this.indeterminateUserSelection = false
      this.selectedAllUsers = false
    } else if (currentUsers.length === this.items.length) {
      this.indeterminateUserSelection = false
      this.selectedAllUsers = true
    } else {
      this.indeterminateUserSelection = true
      this.selectedAllUsers = false
    }
  }

  /**
   * Fill the device info for each user in the grid.
   * This is handle with promise batches to determinate size.
   *
   * @param startIndex user index to start to fill
   * @param batchSize size of the promises to request and wait to resolve
   */
  async updateLicensesRelated (startIndex: number, batchSize: number) {
    let licensePromises: Promise<any>[] = []
    let lastIndex = startIndex
    for (let userIndex = startIndex; userIndex < this.items.length && userIndex < startIndex + batchSize; userIndex++, lastIndex++) {
      let user = this.items[userIndex]
      let licensePromise = this.updateLicensesInfo(user)
      licensePromises.push(licensePromise)
    }

    await Promise.allSettled(licensePromises)
      .then(() => {
        if (lastIndex >= this.items.length) {
          return
        }
        this.updateLicensesRelated(lastIndex, batchSize)
      })
  }

  /**
   * Given a user catalog item updates its license info
   *
   * @param user grid item to update
   */
  async updateLicensesInfo (user: UserCatalogGridItem) {
    user.activeLicenses = []
    let pageRequest = this.prepareRequestForActiveLicenses(user)
    await catalogsService.getLicenses(pageRequest)
      .then((response) => {
        user.activeLicenses.push(...response.results)
        this.updateDevicesInfo(user)
      })
  }

  /* Export data */

  async exportData () {
    this.allUserItems = []
    let pageRequest = this.userPageRequest()
    pageRequest.page = 0
    pageRequest.size = this.PAGE_SIZE
    await this.loadAllUsers(pageRequest)
    this.printCsv()
  }

  printCsv () {
    let csv = ''
      .concat('NOMBRE DE USUARIO').concat(',')
      .concat('ID').concat(',')

    if (this.isRoleWithPermissions) {
      csv = csv.concat('ESTATUS').concat(',')
    }

    csv = csv.concat('DOMINIO').concat(',')
      .concat('CLIENTE').concat(',')
      .concat('ROL').concat(',')
      .concat('NOMBRE').concat(',')
      .concat('APELLIDO(S)').concat(',')
      .concat('CORREO ELECTRONICO').concat(',')
      .concat('TELEFONO').concat(',')
      .concat('GRUPOS').concat(',')
      .concat('CREACION').concat(',')
      .concat('ACTUALIZACION')

    if (this.isRoleWithPermissions) {
      csv = csv.concat(',')
        .concat('BAJA').concat(',')
        .concat('FACTURABLE').concat(',')
        .concat('PERMISOS EXTENDIDOS')
    }

    csv = csv.concat('\n')

    for (let it of this.allUserItems) {
      csv += ''
        .concat(reportsService.formatTextForCsvFile(it.name)).concat(',')
        .concat(it.id.toString()).concat(',')

      if (this.isRoleWithPermissions) {
        csv = csv.concat(it.statusName).concat(',')
      }

      csv = csv.concat(reportsService.formatTextForCsvFile(it.domain)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.client)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.roleName)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.firstName)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.lastName)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.email)).concat(',')
        .concat(it.cellPhone).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.groupsNames)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.creationDateString)).concat(',')
        .concat(reportsService.formatTextForCsvFile(it.updateDateString))

      if (this.isRoleWithPermissions) {
        csv = csv.concat(',')
          .concat(reportsService.formatTextForCsvFile(it.disableDateString)).concat(',')
          .concat(it.billableString.replace('í', 'i')).concat(',')
          .concat(it.extendedPermissionsString.replace('í', 'i'))
      }

      csv = csv.concat('\n')
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `usuarios.csv`
    element.click()
  }
}
